import { _ } from "../../core/polyglot/polyglot.js";
import { extend } from "../../core/helpers/object-util.js";
import {
	findBestFormatForNumbers,
	formatCurrency,
} from "../../core/helpers/currency-formatting-util.js";
import { findLast } from "@foretagsplatsen/core-helpers/array";
import {
	formatAsMonth,
	formatAsMonthAndYear,
} from "../../core/helpers/date-formatting-util.js";
import { getCSSVariable } from "../../core/helpers/css-util.js";
import chartBuilder from "../../core/charts.js";
import chartDisplaySettings from "../../core/charts/chartDisplaySettings.js";
import HighchartWidget from "../../core/widgets/HighchartWidget.js";
import jQuery from "jquery";
import LegendWidget from "../../core/widgets/LegendWidget.js";
import nullPeriod from "../../models/period/nullPeriod.js";
import responsive from "../../core/responsive.js";
import session from "../../core/session.js";

/**
 * Business overview widget displays business progress the latest months.
 * By default it displays monthly profit and accumulated profit but the user
 * can choose to display income and accumulated income.
 *
 * It also functions as a selector to select current month.
 *
 * @param {Period} spec.currentPeriod - The period being represented.
 */

/**
 * TODO: write documentation
 */
export default class BusinessOverviewWidget extends HighchartWidget {
	constructor(spec = {}) {
		super(...arguments);
		this._currentPeriod = spec.currentPeriod || nullPeriod;
		this._chartId = "business-chart-container";
		this._chartXAxisId = "business-overview-xaxis";

		this._formatter = formatCurrency;

		this._legend = new LegendWidget({
			chart: this, // todo: use events instead of coupling the widgets like this?
			legendId: "business-chart-legend",
			legendItemClass: "col-lg-3 col-sm-6 col-xs-6 col-xxs-12",
			itemOptions: [
				{
					titleRenderer: (value) => (html) => {
						if (value < 0) {
							html.render(_("Loss after financial items"));
						} else {
							html.render(_("Profit after financial items"));
						}
					},
					seriesId: "profit",
					onClick: this._setLabelVisibility.bind(this),
					valueRenderer: (value) => this._formatter(value),
				},
				{
					titleRenderer: (value) => (html) => {
						if (value < 0) {
							html.render(
								_("Accumulated loss after financial items"),
							);
						} else {
							html.render(
								_("Accumulated profit after financial items"),
							);
						}
					},
					seriesId: "accumulated-profit",
					onClick: (event, show) => {
						// Hack since highcharts seems to broken
						// The green square remains on screen
						const labels = jQuery(".highcharts-data-labels");
						if (show) {
							jQuery(labels[2]).show();
						} else {
							jQuery(labels[2]).hide();
						}
						this._setLabelVisibility();
					},
					valueRenderer: (value) => this._formatter(value),
				},
				{
					title: _("Income"),
					seriesId: "income",
					active: false,
					onClick: this._setLabelVisibility.bind(this),
					valueRenderer: (value) => this._formatter(value),
				},
				{
					title: _("Accumulated income"),
					seriesId: "accumulated-income",
					active: false,
					onClick: this._setLabelVisibility.bind(this),
					valueRenderer: (value) => this._formatter(value),
				},
			],
		});
	}

	show(data, period) {
		this._setup();
		this._updateHighchartsDiv();

		if (period) {
			this._currentPeriod = period;
		}

		this._updateXAxis();

		const values = [
			data.profitTrend,
			data.accumulatedProfitTrend,
			data.incomeTrend,
			data.accumulatedIncomeTrend,
		];

		this._formatter = findBestFormatForNumbers(values.flat());

		// Make sure we have one entry for each period
		this._extendValuesToContainEntriesForEveryDataPoint(
			values,
			data.periodLabels,
		);

		this.setData(
			values.map((entries, index) => {
				if (index === 1) {
					// index === 1 correspond to the accumulated result series
					const dataWithMarkerSelected = this._buildDataFor(entries);

					// eslint-disable-next-line no-shadow -- bad practice, we should fix this
					dataWithMarkerSelected.forEach((item, index) => {
						if (
							!this._currentPeriod
								.lastMonth()
								.includes(new Date(item.x))
						) {
							return;
						}
						const result = this._findLatestNonNullBeforeItem(
							index,
							dataWithMarkerSelected,
						);
						extend(result, this._pointMarkerSelected);
					});

					return dataWithMarkerSelected;
				}
				return this._buildDataFor(entries);
			}),
		);

		this._legend.setValues(
			data.getValuesForPeriod(this._currentPeriod, this._getPeriods()),
		);
	}

	getCurrentPeriod() {
		return this._currentPeriod;
	}

	_setup() {
		const accumulatedProfitColor = getCSSVariable(
			"--kpi-accumulated-profit-color",
		); // "#9ec94f";
		const incomeColor = getCSSVariable("--kpi-income-color"); // "#424242";
		const accumulatedIncomeColor = getCSSVariable(
			"--kpi-accumulated-income-color",
		); // "#424242";
		const profitColor = getCSSVariable("--kpi-profit-color");

		this._pointMarker = {
			dataLabels: {
				enabled: false,
			},
			marker: {
				symbol: "circle",
				lineWidth: 3,
				lineColor: accumulatedProfitColor,
				fillColor: "#ffffff",
				radius: 6,
			},
		};

		const that = this;

		this._pointMarkerSelected = {
			dataLabels: {
				enabled: true,
				borderRadius: 4,
				backgroundColor: accumulatedProfitColor,
				borderWidth: 0,
				borderColor: accumulatedProfitColor,
				color: "#FFFFFF",
				padding: 20,
				allowOverlap: true,
				centerMarker: true,
				crop: false,
				className: "centered-data-label",
				formatter: function () {
					const { y } = this;
					return that._formatter(y);
				},
				style: {
					textOutline: "none",
					fontFamily: chartDisplaySettings.fontBold,
				},
				y: -7,
			},
			marker: {
				fillColor: accumulatedProfitColor,
				lineColor: accumulatedProfitColor,
			},
		};

		this._chartOptions = chartBuilder()
			.set("plotOptions", {
				series: chartBuilder
					.seriesPlotOptions()
					.setPoint(this._pointMarker)
					.setSelectablePoints({
						onClick: (point) => this._onPlotbandClick(point),
					})
					.setSelectedPoint(this._pointMarkerSelected)
					.build(),
			})
			.set("xAxis", this._createXAxisOptionsBuilder())
			.set("yAxis", chartBuilder.yAxis())
			.set({
				tooltip: {
					formatter: function () {
						const { x, y, series } = this;
						let tooltip = `<b>${formatAsMonthAndYear(x)}</b>`;

						tooltip += `<br/>${that._formatSeriesName(
							series.options,
							y,
						)}: `;

						tooltip += that._formatter(y);

						return tooltip;
					},
				},
			})
			.set("series", [
				chartBuilder
					.series()
					.set({
						id: "profit",
						name: _("Profit").toString(),
						type: "column",
						color: profitColor,
						dataLabels: {
							style: { textOutline: "none" },
							formatter: function () {
								const { y } = this;
								return that._formatter(y);
							},
						},
					})
					.build(),
				chartBuilder
					.scatterSeries()
					.set({
						zIndex: 5,
						id: "accumulated-profit",
						name: _("YTD profit after financial items").toString(),
						lineColor: accumulatedProfitColor,
						dataLabels: {
							formatter: function () {
								const { y } = this;
								return that._formatter(y);
							},
						},
					})
					.set(this._pointMarker)
					.build(),
				chartBuilder
					.series()
					.set({
						id: "income",
						name: _("Income").toString(),
						type: "column",
						color: incomeColor,
						dataLabels: {
							style: { textOutline: "none" },
							formatter: function () {
								const { y } = this;
								return that._formatter(y);
							},
						},
					})
					.set("visible", false)
					.build(),
				chartBuilder
					.scatterSeries()
					.set({
						id: "accumulated-income",
						name: _("Accumulated income").toString(),
						color: accumulatedIncomeColor,
						lineColor: accumulatedIncomeColor,
						dataLabels: {
							enabled: false,
						},
						marker: {
							symbol: "circle",
							lineWidth: 3,
							lineColor: accumulatedIncomeColor,
							fillColor: "#ffffff",
							radius: 6,
						},
					})
					.set("visible", false)
					.build(),
			])
			.build();
	}

	_formatSeriesName(options, y) {
		if (y < 0) {
			if (options.id === "profit") {
				return _("Loss");
			} else if (options.id === "accumulated-profit") {
				return _("YTD loss after financial items");
			}
		}
		return options.name;
	}

	_didAttach() {
		this._setLabelVisibility();
	}

	_onPlotbandClick(point) {
		session.redirectToPeriod(point.options.period);
	}

	_extendValuesToContainEntriesForEveryDataPoint(values, points) {
		values.forEach((entries) => {
			while (entries.length < points.length) {
				entries.push(null);
			}
		});
	}

	_findLatestNonNullBeforeItem(index, data) {
		const trimmedData = data.slice(0, index + 1);

		return findLast(trimmedData, (each) => each.y !== null);
	}

	_buildDataFor(data) {
		const result = [];

		const initialMonth = this._getPeriods()[0];
		const initialYear = initialMonth.getYear();

		const initialMonthIndex = initialMonth.getStart().getMonth();
		const initialYearIndex = initialYear.getStart().getFullYear();

		data.forEach((each, month) => {
			result.push({
				period: this._getPeriods()[month],
				x: new Date(
					initialYearIndex,
					initialMonthIndex + month,
					1,
				).valueOf(),
				y: each,
			});
		});

		return result;
	}

	_getPeriods() {
		return this._currentPeriod.getYear().asMonths();
	}

	_updateXAxis() {
		this._chartOptions.xAxis = this._createXAxisOptionsBuilder().build();
		if (this._highchart) {
			this._highchart
				.get(this._chartXAxisId)
				.update(this._chartOptions.xAxis);
		}
	}

	_createXAxisOptionsBuilder() {
		return chartBuilder
			.xDateAxis()
			.set("id", this._chartXAxisId)
			.set("labels", {
				style: { fontSize: "12px" },
				rotation: responsive.isMobileBreakpoint() ? -90 : 0,
				formatter: function () {
					const { value, isFirst } = this;
					const currentDate = new Date(value);
					if (isFirst || currentDate.getMonth() === 0) {
						return formatAsMonthAndYear(currentDate);
					}
					return formatAsMonth(currentDate);
				},
			})
			.setMonthTickInterval()
			.setPeriods({
				periods: this._getPeriods(),
				selectedPeriod: this._currentPeriod,
			})
			.setSelectablePeriods({
				plotBandClicked: (clickedPlotBand) => {
					this._onPlotbandClick(clickedPlotBand);
				},
			});
	}

	/**
	 * This function has for goal to hide the plot labels on
	 * mobile, except for the accumulated result.
	 */
	_setLabelVisibility() {
		if (!responsive.isMobileBreakpoint()) {
			return;
		}

		if (!this._highchart) {
			return;
		}

		// The series at index 1 is accumulated result
		const visibleSeriesLabelIndex = 1;

		this._highchart.series.forEach((series, index) => {
			if (!series.visible || index === visibleSeriesLabelIndex) {
				return;
			}

			series.data.forEach((datum) => {
				if (!datum.dataLabel) {
					return;
				}
				datum.dataLabel.hide();
			});
		});
	}
}
